import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { UserEntrySchema } from './user_entry.ts'
import { TasksModelFields } from './tasks_model.ts'
import { TaskDealsModelFields } from './task_deals_model.ts'
import { TaskAccountsModelFields } from './task_accounts_model.ts'
import { TaskProspectsModelFields } from './task_prospects_model.ts'
import {
  TABLE_TASKS,
  TABLE_TASK_DEALS,
  TABLE_TASK_ACCOUNTS,
  TABLE_TASK_PROSPECTS,
  TABLE_TASK_ASSIGNEES,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG
} from '../globals.ts'

const ALIAS_TASKS = `${TABLE_TASKS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_TASK_DEALS = `${TABLE_TASK_DEALS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_TASK_ACCOUNTS = `${TABLE_TASK_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_TASK_PROSPECTS = `${TABLE_TASK_PROSPECTS}${FIELD_KEY_SEPARATOR}` as const

const PROG_ASSIGNEES = `${FIELD_KEY_PROG}${TABLE_TASK_ASSIGNEES}` as const

const FIELD_ID = `${ALIAS_TASKS}${TasksModelFields.id}` as const
const FIELD_NOTES = `${ALIAS_TASKS}${TasksModelFields.notes}` as const
const FIELD_DUE_BY = `${ALIAS_TASKS}${TasksModelFields.due_by}` as const
const FIELD_SUBJECT = `${ALIAS_TASKS}${TasksModelFields.subject}` as const
const FIELD_COMPLETED = `${ALIAS_TASKS}${TasksModelFields.completed}` as const
const FIELD_SORT_ORDER = `${ALIAS_TASKS}${TasksModelFields.sort_order}` as const
const FIELD_CREATED_AT = `${ALIAS_TASKS}${TasksModelFields.created_at}` as const
const FIELD_COMPLETED_ON = `${ALIAS_TASKS}${TasksModelFields.completed_on}` as const

const FIELD_TASK_DEALS_ID = `${ALIAS_TASK_DEALS}${TaskDealsModelFields.id}` as const
const FIELD_TASK_DEALS_DEAL_ID = `${ALIAS_TASK_DEALS}${TaskDealsModelFields.deal_id}` as const

const FIELD_TASK_ACCOUNTS_ID = `${ALIAS_TASK_ACCOUNTS}${TaskAccountsModelFields.id}` as const
const FIELD_TASK_ACCOUNTS_ACCOUNT_ID = `${ALIAS_TASK_ACCOUNTS}${TaskAccountsModelFields.account_id}` as const

const FIELD_TASK_PROSPECTS_ID = `${ALIAS_TASK_PROSPECTS}${TaskProspectsModelFields.id}` as const
const FIELD_TASK_PROSPECTS_PROSPECT_ID = `${ALIAS_TASK_PROSPECTS}${TaskProspectsModelFields.prospect_id}` as const

export const TaskRecordSchema = z
  .object({
    // tasks.id is NOT NULL: a task without an id is invalid data, not an optional field
    [FIELD_ID]: z.string(),
    [FIELD_NOTES]: z.string().nullish(),
    [FIELD_DUE_BY]: z.string().nullish(),
    [FIELD_SUBJECT]: z.string().nullish(),
    [FIELD_COMPLETED]: z.boolean().nullish(),
    [FIELD_SORT_ORDER]: z.number().nullish(),
    [FIELD_CREATED_AT]: z.string().nullish(),
    [FIELD_COMPLETED_ON]: z.string().nullish(),
    [FIELD_TASK_DEALS_ID]: z.string().nullish(),
    [FIELD_TASK_DEALS_DEAL_ID]: z.string().nullish(),
    [FIELD_TASK_ACCOUNTS_ID]: z.string().nullish(),
    [FIELD_TASK_ACCOUNTS_ACCOUNT_ID]: z.string().nullish(),
    [FIELD_TASK_PROSPECTS_ID]: z.string().nullish(),
    [FIELD_TASK_PROSPECTS_PROSPECT_ID]: z.string().nullish(),
    [PROG_ASSIGNEES]: z.array(UserEntrySchema).nullish()
  })
  .strict()

export const TaskRecordFields = makeFieldKeys(TaskRecordSchema)

export type TaskRecord = z.infer<typeof TaskRecordSchema>

export function validateTaskRecord(data: unknown, options: { isArray: true }): TaskRecord[]
export function validateTaskRecord(data: unknown, options?: { isArray?: false }): TaskRecord
export function validateTaskRecord(data: unknown, options?: { isArray?: boolean }): TaskRecord | TaskRecord[]
export function validateTaskRecord(data: unknown, options?: { isArray?: boolean }): TaskRecord | TaskRecord[] {
  try {
    return validateWithZodSchema(TaskRecordSchema, data, 'task record', options)
  } catch (error) {
    throw new Error(`Task record validation failed: ${error}`)
  }
}
